import { just, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { warnAndNotify } from '../../../../../../Honeybadger';
import { useLearningCollection } from '../LearningCollectionContext';
import { AllExperiencesResource, ExperienceResourceArray } from '../Types';
import { FilterOptionData } from '../../Builder/ExperienceCatalog/Types';
import { FilterStores } from './FilterStores';
import { useFilterOptions } from './useFilterOptions';
import { FilterOptionsPayload } from './FilterOptionsTypes';
import { persistentFilterStore } from './PersistentFilterStore';
import { calculateFilterOptions, licensedFilterOptions } from './Utils';

const emptyFilterOptions: FilterOptionsPayload = {
  product_type_options: [],
  duration_options: [],
  school_partner_options: [],
  language_options: [],
  license_options: [],
  show_unlicensed_toggle: 'hide',
};

const values = (options: FilterOptionData[]): string[] => options.map((option) => option.value);

interface ReadyProps {
  experiences: ExperienceResourceArray;
  filterOptions: FilterOptionsPayload;
  children: React.ReactNode;
}

function ReadyFilterContextView({ experiences, filterOptions, children }: ReadyProps) {
  // Stores are keyed on the options for every course, so flipping the
  // unlicensed toggle keeps the stores (and the toggle itself) in place.
  const allOptions = calculateFilterOptions(experiences, filterOptions);

  // Get or create filter stores from the persistent store
  // This maintains filter state across component unmounts/remounts
  const stores = persistentFilterStore.getOrCreateStores(
    allOptions.productTypeOptions,
    allOptions.schoolPartnerOptions,
    allOptions.languageOptions,
    allOptions.durationOptions,
    allOptions.licenseOptions,
    allOptions.competencyOptions,
  );

  // The options on offer follow the toggle, like the course list does
  const options = stores.unlicensedToggleStore.showUnlicensed
    ? allOptions
    : licensedFilterOptions(experiences, filterOptions);

  useEffect(() => {
    stores.productTypeFilterStore.keepOnly(values(options.productTypeOptions));
    stores.schoolPartnerFilterStore.keepOnly(values(options.schoolPartnerOptions));
    stores.languageFilterStore.keepOnly(values(options.languageOptions));
    stores.durationFilterStore.keepOnly(values(options.durationOptions));
    stores.licenseFilterStore.keepOnly(values(options.licenseOptions));
    stores.competencyFilterStore.keepOnly(values(options.competencyOptions));
  }, [stores, options]);

  const filterContextValue = {
    productTypeFilterStore: stores.productTypeFilterStore,
    durationFilterStore: stores.durationFilterStore,
    schoolPartnerFilterStore: stores.schoolPartnerFilterStore,
    languageFilterStore: stores.languageFilterStore,
    licenseFilterStore: stores.licenseFilterStore,
    competencyFilterStore: stores.competencyFilterStore,
    sortStore: stores.sortStore,
    ...options,
    unlicensedToggleStore: stores.unlicensedToggleStore,
    showUnlicensedToggle: filterOptions.show_unlicensed_toggle,
  };

  return <FilterStores.Provider value={just(filterContextValue)}>{children}</FilterStores.Provider>;
}

const ReadyFilterContext = observer(ReadyFilterContextView);

interface Props {
  resource: AllExperiencesResource;
  children: React.ReactNode;
}

const FilterContext: React.FC<Props> = ({ resource, children }) => {
  // Get the learning collection template resource which has filter_options link with product_license_id
  const { resourceState } = useLearningCollection();

  // Get server-provided filter options using the template resource links (includes product_license_id)
  const { data: filterOptions, isLoading, isError, error } = useFilterOptions(resourceState.links);

  // Alert Honeybadger if filter options fail to load
  useEffect(() => {
    if (isError) {
      warnAndNotify('FilterOptionsLoadError', 'Failed to load filter options for BuilderV2', {
        error,
      });
    }
  }, [isError, error]);

  // If filter options are still loading, hide everything until they arrive
  if (isLoading) {
    return <FilterStores.Provider value={nothing()}>{children}</FilterStores.Provider>;
  }

  // Fall back to empty options when the filter_options link is missing or the request errors,
  // so the experience list still renders rather than going blank
  return (
    <ReadyFilterContext
      experiences={resource.payload.experiences}
      filterOptions={filterOptions ?? emptyFilterOptions}
    >
      {children}
    </ReadyFilterContext>
  );
};

export default observer(FilterContext);
